import { useRef } from 'react';

import { BadgeColor } from 'types';
import { useAppDispatch } from 'store';
import { Badge } from 'components/badge';
import { useSelectedCloverDevice } from 'hooks';
import { Select } from 'components/select/select';
import { isSingleSelectValue } from 'utils/helpers';
import { setSelectedDevice } from 'store/slices/clover-devices';
import {
  asStale,
  findReader,
  ReaderStatus,
  isHeldByOther,
  RECONNECTING_WORD,
} from 'components/reader-status';
import { isConfirmedOffline } from 'components/checkout-payment-methods/credit/reader-offline-check';

import * as Styles from './styles';
import { useFitName } from './use-fit-name';
import { SelectCloverDeviceProps } from './types';
import type { Reader } from 'store/api/clover/reader-status';
import { useEventReaders } from './use-event-readers';

export const SelectCloverDevice = ({
  part,
  event,
  label,
  className,
  showReaderStatus,
}: SelectCloverDeviceProps) => {
  const dispatch = useAppDispatch();
  const { deviceOptions, selectedDevice } = useSelectedCloverDevice(event);

  const isMultiDevices = deviceOptions?.length > 1;
  // A part that draws nothing for this reader count reads nothing either, so
  // the cart's chip and picker never ask for the status twice.
  const drawsNothing =
    (part === 'chip' && isMultiDevices) ||
    (part === 'picker' && !isMultiDevices);
  const live = useEventReaders(event?.id, !showReaderStatus || drawsNothing);
  const { readers, stale } = live;
  // Owner 24 Oct ("keep it live"): an old list is never shown green, and
  // blocks nothing (the server still refuses a reader in use).
  const shown = (reader?: Reader) =>
    reader && stale ? asStale(reader) : reader;

  const selectedValue = deviceOptions?.find(
    (value) => value.value === selectedDevice?.id
  );

  // One reader: the picker stays hidden, as before; the cart shows a small
  // chip "<name> · <word> ●" instead. Nothing when the status can't be read.
  const reader =
    showReaderStatus && !isMultiDevices
      ? shown(findReader(readers, selectedDevice?.id))
      : undefined;
  // The live channel is down: a small note (the 5 s read still runs).
  const reconnectNote =
    showReaderStatus && live.reconnecting && !stale ? (
      <Styles.Reconnecting role="status" data-testid="reader-reconnecting">
        {RECONNECTING_WORD}
      </Styles.Reconnecting>
    ) : null;
  const chipRef = useRef<HTMLDivElement>(null);
  const chipName = selectedDevice?.name || reader?.name;
  const shortName = useFitName(chipRef, chipName, reader?.word);

  if (drawsNothing) {
    return null;
  }

  if (!isMultiDevices) {
    if (!reader) {
      return null;
    }
    return (
      <Styles.Chip
        ref={chipRef}
        className={className}
        data-testid="cart-reader-chip"
      >
        {reconnectNote}
        <Badge
          bold
          smallFont
          styleType={BadgeColor.LightGrey}
          text={
            <ReaderStatus
              dotAfter
              reader={reader}
              label={chipName}
              testId="cart-reader"
              shortLabel={shortName}
            />
          }
        />
      </Styles.Chip>
    );
  }

  const picker = (
    <Select
      className={className}
      value={selectedValue}
      options={deviceOptions}
      placeholder="Select Device"
      label={label || 'Select Device'}
      onChange={(value) => {
        if (isSingleSelectValue(value)) {
          dispatch(
            setSelectedDevice({
              eventId: event.id,
              deviceId: Number(value?.value),
            })
          );
        }
      }}
      {...(showReaderStatus && {
        // Owner 22 Oct 2026: a reader Clover reports offline (or with Cloud
        // Pay Display closed) cannot be picked for a card sale; v2: only on a
        // fresh, Clover-confirmed answer (not stale, checked in the last
        // 30 s). The list is read again whenever the cart opens.
        // Reader hold (owner 24 Oct): a reader in another rep's sale or held
        // by another rep's cart is red and can't be picked (live list only).
        isOptionDisabled: (option: { value?: string | number | null }) => {
          const found = findReader(readers, option.value);
          return !stale && (isConfirmedOffline(found) || isHeldByOther(found));
        },
        // Two or more readers: a dot beside each name, and the Devices
        // screen's word for every reader that is not Ready (owner 22 Oct:
        // two red dots must not look the same when only one can be picked).
        formatOptionLabel: (option) => {
          const reader = shown(findReader(readers, option.value));
          return reader ? (
            <ReaderStatus
              dotAfter
              reader={reader}
              nameOnly={option.label}
              testId={`reader-option-${option.value}`}
              note={
                reader.status === 'ready' && reader.word !== RECONNECTING_WORD
                  ? undefined
                  : reader.word
              }
            />
          ) : (
            option.label
          );
        },
      })}
    />
  );

  return reconnectNote ? (
    <Styles.PickerWrap>
      {picker}
      {reconnectNote}
    </Styles.PickerWrap>
  ) : (
    picker
  );
};
